import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useNavigation, useSearchParams } from "react-router-dom";
import { Input, Image, Button, Form, Select, message, DatePicker, InputNumber } from "antd";
import { v4 as uuidv4 } from "uuid";
import { ApolloError } from "@apollo/client";
import dayjs from "dayjs";

import {
	CreateInstalledProductInput,
	Installed_Product_Attachments_Insert_Input,
	Products,
	Properties,
	Maybe
} from "graphql/generated/graphql";
import { useGetProductsByOrganizationIdQuery } from "graphql/queries/get-products-by-organization-id.generated";
import { useGetUserPropertiesFilteredQuery } from "graphql/queries/get-user-properties-filtered.generated";
import { useCreateInstalledProductMutation } from "graphql/mutations/create-installed-product.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useSelectedAttachments, clearSelectedAttachments } from "state/reactive-vars/selected-attachment-var";

import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import AttachmentItem from "components/attachment-item/attachment-item";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import LocationHierarchySelect, {
	LocationHierarchyValue,
	emptyLocationHierarchyValue
} from "components/location-hierarchy-select/location-hierarchy-select";

import { InstalledProductWarrantyStatus } from "constants/field-values";
import routeNames from "constants/route-names";
import { pruneEmptyValues } from "utils/helpers";
import { getFileExtension, uploadToStorage } from "utils/files-management";
import { RcFile } from "antd/es/upload/interface";
import UploadPhotoModal from "components/upload-photo-modal/upload-photo-modal";

const { TextArea } = Input;

const InstalledProductsAdd: React.FC = () => {
	const navigate = useNavigate();
	const t = useNavigation();
	const [searchParams] = useSearchParams();
	const preselectedDeviceId = searchParams.get("deviceId");
	const preselectedPropertyId = searchParams.get("propertyId");
	const { loggedUserValue } = useLoggedUser();
	const { updateSelectedAttachments } = useSelectedAttachments();
	const [serial, setSerial] = useState<string>("");
	const [installationDate, setInstallationDate] = useState<dayjs.Dayjs | null>(null);
	const [warrantyExpirationDate, setWarrantyExpirationDate] = useState<dayjs.Dayjs | null>(null);
	const [installationLocation, setInstallationLocation] = useState<string>("");
	const [locationHierarchy, setLocationHierarchy] = useState<LocationHierarchyValue>(emptyLocationHierarchyValue);
	const [installationNotes, setInstallationNotes] = useState<string>("");
	const [uploading, setUploading] = useState(false);
	const [addAttachmentModalOpen, setAddAttachmentModalOpen] = useState<boolean>(false);
	const [addAttachmentModalEditMode, setAddAttachmentModalEditMode] = useState<boolean>(false);
	const [attachmentsFiles, setAttachmentsFiles] = useState<Array<any>>([]);
	const [attachments, setAttachments] = useState<Installed_Product_Attachments_Insert_Input[]>([]);
	const [installedProductInputCompleted, setInstalledProductInputCompleted] = useState(false);

	// Handle product or installed product photo
	const [addProductPhotoModalOpen, setAddProductPhotoModalOpen] = useState<boolean>(false);
	const [installedProductPhotoThumbUrl, setInstalledProductPhotoThumbUrl] = useState<string>("");
	const [installedProductPhotoFile, setInstalledProductPhotoFile] = useState<RcFile | null>(null);

	const [productOptions, setProductOptions] = useState<Array<Products>>([]);
	const [selectedProduct, setSelectedProduct] = useState<Maybe<Products>>(null);
	const [propertyOptions, setPropertyOptions] = useState<Array<Properties>>([]);
	const [selectedProperty, setSelectedProperty] = useState<Maybe<Properties>>(null);
	const handleOnCompleteGetProducts = useCallback(async data => {
		const { products } = data;

		const options = products.map(element => {
			return { value: JSON.stringify(element), label: element.name };
		});

		setProductOptions(options);

		if (preselectedDeviceId) {
			const selectedProduct = products.find(element => element.id === preselectedDeviceId);
			setSelectedProduct(selectedProduct);
		}
	}, []);

	const handleOnErrorGetProducts = useCallback((error: ApolloError) => {
		console.log("handleOnErrorGetProducts Error: ", error);
	}, []);

	const { loading: loadingProducts } = useGetProductsByOrganizationIdQuery({
		variables: {
			organization_id: loggedUserValue.organization_id
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onCompleted: handleOnCompleteGetProducts,
		onError: handleOnErrorGetProducts
	});

	const handleOnCompletePropertiesFiltered = useCallback(
		data => {
			const { user_properties } = data;

			const userProperties = user_properties.map(up => up.property);

			const options = userProperties.map(up => ({
				value: JSON.stringify(up),
				label: up.name
			}));

			setPropertyOptions(options);

			if (preselectedPropertyId) {
				const selectedProperty = userProperties.find(element => element.id === preselectedPropertyId);
				setSelectedProperty(selectedProperty);
			}
		},
		[preselectedPropertyId]
	);

	const handleOnErrorPropertiesFiltered = useCallback((error: ApolloError) => {
		console.log("handleOnErrorPropertiesFiltered Error: ", error);
	}, []);

	const { loading: propertiesLoading } = useGetUserPropertiesFilteredQuery({
		variables: {
			where: {
				user_id: { _eq: loggedUserValue.id },
				property: { deleted_at: { _is_null: true } },
				_or: [
					{ property: { created_by_handover: { status: { _eq: "PUBLISHED" } } } },
					{ property: { created_by_handover_id: { _is_null: true } } },
				]
			}
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorPropertiesFiltered,
		onCompleted: handleOnCompletePropertiesFiltered
	});

	const handleOnCompleteCreateInstalledProductMutation = useCallback(
		(data: any) => {
			let path = routeNames.ADMIN_DASHBOARD;

			// preselectedDeviceId currently only possible from admin product details
			if (preselectedDeviceId) {
				path = routeNames.ADMIN_DEVICES_DETAILS.replace(":deviceId", preselectedDeviceId);
			} else if (selectedProperty) {
				path = routeNames.ADMIN_PROPERTY_DETAILS.replace(":propertyId", selectedProperty.id);
			}

			navigate(path);
		},
		[preselectedDeviceId, selectedProperty]
	);

	const handleOnErrorCreateInstalledProductMutation = useCallback((error: ApolloError) => {
		console.log("Error Add Installed Product Error: ", error);
	}, []);

	const [createInstalledProductMutation, { data, loading: loadingCreateInstalledProducts, error }] =
		useCreateInstalledProductMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorCreateInstalledProductMutation,
			onCompleted: handleOnCompleteCreateInstalledProductMutation
		});

	const handleOnCloseAttachmentModal = useCallback(async () => {
		setAddAttachmentModalOpen(false);
		setAddAttachmentModalEditMode(false);
		clearSelectedAttachments();
	}, []);

	const handleOnRemoveAttachment = useCallback(
		(attachmentFile: any) => {
			const { uid, type, description, file } = attachmentFile;

			const newList = attachmentsFiles.filter(element => element.uid !== uid);

			if (attachmentsFiles) {
				setAttachmentsFiles(newList);
			}
		},
		[attachmentsFiles]
	);

	const handleOnEditAttachment = useCallback(
		(attachmentFile: any) => {
			const { uid, type, description, file, name } = attachmentFile;

			updateSelectedAttachments(attachmentFile);

			setAddAttachmentModalEditMode(true);
			setAddAttachmentModalOpen(true);
		},
		[updateSelectedAttachments]
	);

	const updateAttachments = useCallback((attachmentFile: any) => {
		const { type, uid } = attachmentFile;

		setAttachmentsFiles(prev => {
			const filteredList = prev.filter(element => element.uid !== uid);

			return filteredList.concat([attachmentFile]);
		});
	}, []);

	const uploadAttachments = useCallback(async () => {
		if (attachmentsFiles.length > 0) {
			setUploading(true);

			try {
				const promises: Promise<Installed_Product_Attachments_Insert_Input | undefined>[] = [];

				attachmentsFiles.forEach((attachment, index) => {
					const { type, description, file, name } = attachment;

					const fileMediaType = file.type.split("/")[0];
					const uniqueID = uuidv4();

					let bucketName = "installed-product-attachments/";
					let attachmentName = "";
					const fileExtension = getFileExtension(file.name);
					let fileName = name ? `${name}.${fileExtension}` : file.name;

					const newAttachment: Installed_Product_Attachments_Insert_Input = {
						photo_link: null,
						document_link: null,
						video_link: null,
						audio_link: null,
						type,
						description,
						name,
						mime_type: file.type
					};

					switch (fileMediaType) {
						case "image":
							bucketName = bucketName + "photos";
							attachmentName = `attachment-photo-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						case "video":
							bucketName = bucketName + "videos";
							attachmentName = `attachment-video-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						case "audio":
							bucketName = bucketName + "audios";
							attachmentName = `attachment-audio-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						default:
							bucketName = bucketName + "documents";
							attachmentName = `attachment-document-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
					}

					const uploadTask = async () => {
						try {
							const fileObj = file?.originFileObj;

							const { publicUrl } = await uploadToStorage(fileObj, () => {}, `${bucketName}/${attachmentName}`)

							switch (fileMediaType) {
								case "image":
									newAttachment.photo_link = publicUrl;
									break;
								case "video":
									newAttachment.video_link = publicUrl;
									break;
								case "audio":
									newAttachment.audio_link = publicUrl;
									break;
								default:
									newAttachment.document_link = publicUrl;
									break;
							}

							return newAttachment;
						} catch (error) {
							console.log("Error when trying to upload the attachment file to firestore. Error: ", error);
						}
					};

					promises.push(uploadTask());
				});

				try {
					const promisesResults = await Promise.all(promises);

					const filteredPromisesResults = pruneEmptyValues(
						promisesResults
					) as Installed_Product_Attachments_Insert_Input[];

					setAttachments(filteredPromisesResults);
				} catch (error) {
					console.log("Error trying to upload attachments: ", error);
				}
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
			} finally {
				setUploading(false);
			}
		}

		return;
	}, [attachmentsFiles]);

	const uploadProductImg = useCallback(async () => {
		if (installedProductPhotoFile) {
			try {
				const productName = selectedProduct?.name;
				const uniqueID = uuidv4();

				const bucketName = "installed-product-photos/";

				const fileName = `installed-product-photo${productName ? `-${productName}` : ""
					}-${uniqueID}-${Math.floor(Date.now() / 1000)}-${installedProductPhotoFile.name}`;

				const { publicUrl } = await uploadToStorage(installedProductPhotoFile, () => {}, `${bucketName}/${fileName}`)

				setInstalledProductPhotoThumbUrl(publicUrl);
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
			}
		}
	}, [installedProductPhotoFile, selectedProduct]);

	// Handle installed product photo
	const handleOnConfirmPhoto = useCallback((file: any, url: string) => {
		setInstalledProductPhotoThumbUrl(url);
		setInstalledProductPhotoFile(file);
	}, []);

	const handleOnRemovePhoto = useCallback(async () => {
		// Clear local state
		setInstalledProductPhotoThumbUrl("");
		setInstalledProductPhotoFile(null);
	}, []);

	const handleOnSubmit = useCallback(async () => {
		if (selectedProperty && selectedProduct) {
			if (installedProductPhotoFile) {
				await uploadProductImg();
			}

			if (attachmentsFiles.length) {
				await uploadAttachments();
			}

			setInstalledProductInputCompleted(true);
		}
	}, [attachmentsFiles, selectedProperty, selectedProduct, installedProductPhotoFile]);

	const uploadProduct = useCallback(async () => {
		if (selectedProperty && selectedProduct) {
			let installedProductInput: CreateInstalledProductInput = {
				product_id: selectedProduct.id,
				property_id: selectedProperty.id,
				product_serial: serial ? serial : null,
				location: installationLocation,
				// Only the deepest selected level is sent; the backend trigger derives the ancestors.
				building_id: locationHierarchy.buildingId,
				floor_id: locationHierarchy.floorId,
				room_id: locationHierarchy.roomId,
				product_notes: installationNotes,
				warranty_status: InstalledProductWarrantyStatus.Active,
				installation_date: null,
				photo_link: installedProductPhotoThumbUrl
			};

			if (installationDate) {
				installedProductInput.installation_date = installationDate.format("YYYY-MM-DDTHH:mm:ssZ");
			}

			if (warrantyExpirationDate) {
				installedProductInput.warranty_expiration_date = warrantyExpirationDate.format("YYYY-MM-DDTHH:mm:ssZ");
			}

			if (attachments && attachments.length > 0) {
				installedProductInput.attachments = attachments;
			}

			createInstalledProductMutation({
				variables: {
					input: installedProductInput
				}
			});
		}
	}, [
		selectedProperty,
		selectedProduct,
		serial,
		installationDate,
		warrantyExpirationDate,
		installationLocation,
		installationNotes,
		attachmentsFiles,
		attachments,
		loggedUserValue,
		installedProductPhotoThumbUrl
	]);

	useEffect(() => {
		if (installedProductInputCompleted) {
			uploadProduct();
		}
	}, [installedProductInputCompleted]);

	const isLoading = uploading || loadingProducts || propertiesLoading || loadingCreateInstalledProducts;

	return (
		<div className="add-product-screen-container">
			<span className="add-product-page-title">{"Add Installed Devices"}</span>
			<div className="add-product-form">
				<span className="add-product-form-title">{"Select a Property"}</span>
				<Form.Item
					className="select-category-form-item"
					rules={[
						{
							required: true,
							message: "Please select a property."
						}
					]}
					required
					validateTrigger="onBlur"
				>
					<Select
						value={selectedProperty ? JSON.stringify(selectedProperty) : undefined}
						defaultActiveFirstOption={false}
						className="add-product-attachment-input"
						onChange={value => {
							const objectValue = JSON.parse(value);
							setSelectedProperty(objectValue);
							// The hierarchy is scoped to the property, so any prior selection no longer applies.
							setLocationHierarchy(emptyLocationHierarchyValue);
						}}
						options={propertyOptions}
						placeholder={"Select Property"}
						allowClear
						disabled={isLoading}
						suffixIcon={
							<img
								src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
								alt="dropdown icon"
								className="chevron-button"
							/>
						}
					/>
				</Form.Item>
			</div>

			{!preselectedDeviceId && (
				<div className="add-product-form">
					<span className="add-product-form-title">{"Select a Device"}</span>
					<Form.Item
						className="select-category-form-item"
						rules={[
							{
								required: true,
								message: "Please select a Device."
							}
						]}
						required
						validateTrigger="onBlur"
					>
						<Select
							value={selectedProduct ? JSON.stringify(selectedProduct) : undefined}
							defaultActiveFirstOption={false}
							className="add-product-attachment-input"
							onChange={value => {
								const objectValue = JSON.parse(value);
								setSelectedProduct(objectValue);
							}}
							options={productOptions}
							placeholder={"Select Device"}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</Form.Item>
				</div>
			)}

			{selectedProduct ? (
				<Form className="add-product-form" autoComplete="off">
					<span className="add-product-form-title">{"Device Details"}</span>
					<Form.Item className="installed-products-add-form-item">
						<Input
							name="name"
							placeholder="Device Name"
							className="add-product-input-pair"
							value={selectedProduct.name}
							autoComplete="off"
							allowClear
							disabled={true}
						/>
					</Form.Item>

					<div className="two-inputs-container">
						<Form.Item className="installed-products-add-form-item-pair">
							<Input
								name="make"
								placeholder="Make"
								className="add-product-input-pair"
								value={selectedProduct.make}
								autoComplete="off"
								allowClear
								disabled={true}
							/>
						</Form.Item>

						<Form.Item className="installed-products-add-form-item-pair">
							<Input
								name="model"
								placeholder="Model"
								className="add-product-input-pair"
								value={selectedProduct.model}
								autoComplete="off"
								allowClear
								disabled={true}
							/>
						</Form.Item>
					</div>

					<div className="two-inputs-container">
						<Form.Item className="installed-products-add-form-item-pair">
							<Input
								name="sku"
								placeholder="SKU"
								className="add-product-input-pair"
								value={selectedProduct.sku || ""}
								autoComplete="off"
								allowClear
								disabled={true}
							/>
						</Form.Item>

						<Form.Item className="installed-products-add-form-item-pair">
							<Select
								value={selectedProduct.type}
								className="add-product-input-select"
								placeholder="Device Type"
								allowClear
								disabled={true}
							/>
						</Form.Item>
					</div>

					<Form.Item className="installed-products-add-form-item">
						<InputNumber
							name="retailPrice"
							placeholder="Retail Price"
							className="add-product-input-pair"
							value={selectedProduct.retail_price}
							disabled={true}
						/>
					</Form.Item>

					<Form.Item className="installed-products-add-form-item">
						<TextArea
							name="description"
							placeholder="Description"
							style={{ height: "148px" }}
							value={selectedProduct.description}
							autoComplete="off"
							allowClear
							disabled={true}
						/>
					</Form.Item>
					<div className="two-inputs-container">
						<Form.Item
							className="installed-products-add-form-item-pair"
							rules={[
								{
									required: true,
									message: "Please input the Serial Number of the Device."
								}
							]}
							required
							validateTrigger="onBlur"
						>
							<Input
								name="serial"
								placeholder="Serial Number"
								className="add-product-input-pair"
								value={serial}
								autoComplete="off"
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									setSerial(event.target.value);
								}}
								allowClear
								disabled={isLoading}
							/>
						</Form.Item>

						<Form.Item
							className="installed-products-add-form-item-pair"
							rules={[
								{
									required: true,
									message: "Please input the Installation Date of the Device."
								}
							]}
							required
							validateTrigger="onBlur"
						>
							<DatePicker
								allowClear
								className="installation-date"
								format={"MM/DD/YYYY"}
								placeholder={"Installation Date"}
								onChange={(date, dateString) => {
									if (date) {
										setInstallationDate(date);
									} else {
										setInstallationDate(null);
									}
								}}
								disabled={isLoading}
							/>
						</Form.Item>
					</div>

					<Form.Item className="installed-products-add-form-item">
						<DatePicker
							allowClear
							className="installation-date"
							format={"MM/DD/YYYY"}
							placeholder={"Warranty Expiration Date"}
							onChange={(date, dateString) => {
								if (date) {
									setWarrantyExpirationDate(date);
								} else {
									setWarrantyExpirationDate(null);
								}
							}}
							disabled={isLoading}
						/>
					</Form.Item>

					<Form.Item className="installed-products-add-form-item">
						<LocationHierarchySelect
							propertyId={selectedProperty?.id}
							value={locationHierarchy}
							onChange={setLocationHierarchy}
							disabled={isLoading}
						/>
					</Form.Item>

					<Form.Item className="installed-products-add-form-item">
						<TextArea
							placeholder="Installation Location"
							value={installationLocation}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
								setInstallationLocation(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</Form.Item>

					<Form.Item className="installed-products-add-form-item">
						<TextArea
							placeholder="Notes"
							style={{ height: "137px" }}
							value={installationNotes}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
								setInstallationNotes(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</Form.Item>

					<Button className="upload-product-button" onClick={() => setAddProductPhotoModalOpen(true)}>
						Upload Device Image
					</Button>

					{installedProductPhotoThumbUrl ? (
						<div className="organization-button-container">
							<Image width={170} src={installedProductPhotoThumbUrl} />{" "}
							<img
								alt={`remove-organization-photo-icon`}
								src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
								className="organization-photo-action-icon"
								onClick={() => {
									if (!isLoading) {
										handleOnRemovePhoto();
									}
								}}
							/>
						</div>
					) : null}

					<UploadPhotoModal
						open={addProductPhotoModalOpen}
						onCancel={() => setAddProductPhotoModalOpen(false)}
						uploadMethod={handleOnConfirmPhoto}
						disableCrop
					/>
				</Form>
			) : null}

			<div className="add-product-form">
				<span className="add-product-form-title">{"Device Resources"}</span>

				<div className="add-product-documents">
					<Button
						className="upload-product-button"
						onClick={() => setAddAttachmentModalOpen(true)}
						disabled={isLoading}
					>
						Add Resources
					</Button>
				</div>

				{attachmentsFiles && attachmentsFiles.length > 0 ? (
					<div className="attachments-container ">
						{attachmentsFiles.map(attachmentFile => {
							const uid = attachmentFile.uid || attachmentFile.id;

							return (
								<AttachmentItem
									key={`product-attachment-item-${uid}`}
									name={attachmentFile.name}
									file={attachmentFile.file}
									type={attachmentFile.type}
									onRemove={() => {
										if (!isLoading) {
											handleOnRemoveAttachment(attachmentFile);
										}
									}}
									showEditButton
									onEdit={() => {
										if (!isLoading) {
											handleOnEditAttachment(attachmentFile);
										}
									}}
								/>
							);
						})}
					</div>
				) : null}
			</div>

			<div className="add-product-button-container">
				<Button
					className="admin-add-product-button"
					htmlType="submit"
					onClick={handleOnSubmit}
					disabled={isLoading || !selectedProperty || !selectedProduct}
				>
					Save
				</Button>
			</div>

			<AddAttachmentModal
				open={addAttachmentModalOpen}
				onCancel={handleOnCloseAttachmentModal}
				updateAttachments={updateAttachments}
				editMode={addAttachmentModalEditMode}
				editProduct={false}
			/>
			<LoadingOverlay isLoading={isLoading} />
		</div>
	);
};

export default InstalledProductsAdd;
